import Page from "src/components/Page";
import ListFilters, { FilterFieldItem } from "src/modules/shared/components/ListFilters";
import SystemLogsList from "src/modules/systemLogs/List";
import { FC, useEffect, useState } from "react";
import { Box, Container, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FilterIcon } from "src/theme/overrides/CustomIcons";
import { FilterQueryParams, parseQueryParams } from "src/utils/other";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@mui/material";
import { useMount } from "src/hooks/useMount";
import { PATH_SYSTEM_LOGS } from "src/routes/paths";
import { useUserAutosugest } from "src/modules/user/hooks/CompanyUser/useUserAutosuggest";
import { Badge } from "@mui/material";

const SystemLogsListPage: FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const searchString = searchParams.toString();

    const [companyUserName, setCompanyUserName] = useState<string>("");
    const { data: users, refetch, isLoading } = useUserAutosugest(companyUserName);

    const filterFields: FilterFieldItem[] = [
        {
            key: "companyUserId",
            label: t("systemLogs.list.companyUserId"),
            type: "autocomplete",
            fetchDataCallback: (inputValue: string) => {
                setCompanyUserName(inputValue);
            },
            isLoading,
            autocompleteOptions: users,
        },
        {
            key: "operationType",
            label: t("systemLogs.list.operationType"),
            type: "select",
            options: [
                { key: "insert", label: t("systemLogs.list.operationTypeinsert") },
                { key: "update", label: t("systemLogs.list.operationTypeupdate") },
                { key: "delete", label: t("systemLogs.list.operationTypedelete") },
                { key: "custom", label: t("systemLogs.list.operationTypecustom") },
            ],
        },
        {
            key: "entityType",
            label: t("systemLogs.list.entityType"),
            type: "select",
            options: [
                {
                    key: `App\\Entity\\Company\\Company`,
                    label: t("systemLogs.list.objectType.Company"),
                },
                {
                    key: `App\\Entity\\Company\\CompanyConfiguration`,
                    label: t("systemLogs.list.objectType.CompanyConfiguration"),
                },
                {
                    key: `App\\Entity\\CompanyCustomer\\CompanyCustomer`,
                    label: t("systemLogs.list.objectType.CompanyCustomer"),
                },
                {
                    key: `App\\Entity\\CompanyCustomer\\EmployeeCustomer`,
                    label: t("systemLogs.list.objectType.EmployeeCustomer"),
                },
                {
                    key: `App\\Entity\\CompanyCustomer\\CompanyCustomer`,
                    label: t("systemLogs.list.objectType.CompanyCustomer"),
                },
                {
                    key: `App\\Entity\\Company\\CompanyUser`,
                    label: t("systemLogs.list.objectType.CompanyUser"),
                },
                {
                    key: `App\\Entity\\Company\\UserInvitation`,
                    label: t("systemLogs.list.objectType.UserInvitation"),
                },
                {
                    key: `App\\Entity\\Invoice\\ChargingInvoice`,
                    label: t("systemLogs.list.objectType.ChargingInvoice"),
                },
                {
                    key: `App\\Entity\\Location\\Location`,
                    label: t("systemLogs.list.objectType.Location"),
                },
                {
                    key: `App\\Entity\\Station\\Station`,
                    label: t("systemLogs.list.objectType.Station"),
                },
                {
                    key: `App\\Entity\\Tariff\\Tariff`,
                    label: t("systemLogs.list.objectType.Tariff"),
                },
            ],
        },
        { key: "entityId", label: t("shared.filter.entityId"), type: "text" },
        { key: "dateFrom", label: t("shared.filter.from"), type: "date" },
        { key: "dateTo", label: t("shared.filter.to"), type: "date" },
    ];

    const autocompleteFields = filterFields.filter((ff) => ff.type === "autocomplete");

    const [filters, setFilters] = useState<FilterQueryParams>(
        searchString ? parseQueryParams(searchString) : {}
    );

    useMount(() => {
        const currentSearchParams = searchParams.toString();
        autocompleteFields.forEach((af) => {
            searchParams.delete(af.key);
        });
        if (currentSearchParams !== searchParams.toString()) {
            navigate(PATH_SYSTEM_LOGS + "?" + searchParams.toString());
            navigate(0);
        }
    });

    useEffect(() => {
        if (companyUserName) {
            refetch();
        }
    }, [companyUserName]);

    return (
        <Page title={t("systemLogs.list.title")}>
            <Container maxWidth={"xl"}>
                <Box
                    sx={{
                        mb: { xs: 2, sm: 4 },
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                    }}
                >
                    <Stack direction="row" spacing={4} alignItems="center">
                        <Typography variant="h3" component="h1">
                            {t("systemLogs.list.title")}
                        </Typography>
                    </Stack>
                    <Box sx={{ pr: 3 }}>
                        <IconButton onClick={() => setFiltersVisible(!filtersVisible)}>
                            <Badge
                                badgeContent={
                                    Object.keys(filters).filter((key) => filters[key])?.length ||
                                    undefined
                                }
                                color="error"
                            >
                                <FilterIcon
                                    fontSize="small"
                                    color={filtersVisible ? "primary" : undefined}
                                />
                            </Badge>
                        </IconButton>
                    </Box>
                </Box>
                <ListFilters
                    fields={filterFields}
                    isVisible={filtersVisible}
                    handleFilter={(filters) => setFilters(filters)}
                    handleClear={() =>
                        setFilters({ dateFrom: filters.dateFrom, dateTo: filters.dateTo })
                    }
                />
                <Card variant="mainTable">
                    <SystemLogsList filters={filters} />
                </Card>
            </Container>
        </Page>
    );
};

export default SystemLogsListPage;
